import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import StatusBadge, { STATUS_LABELS } from "../../components/StatusBadge";
import { History, ChevronRight, MapPin, Archive, ArchiveRestore, HelpCircle, Building2, UserCircle, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";

const STATUS_GUIDE = [
  { code: "DRAFT", label: "À finaliser", color: "bg-gray-100 text-gray-700 border-gray-200", desc: "Le client a calculé un prix mais n'a pas encore complété les détails (destinataire, accès…). La commande est en pause côté client." },
  { code: "PENDING_OTP", label: "Validation en cours", color: "bg-purple-50 text-purple-800 border-purple-200", desc: "Le client a accepté l'offre et reçu un code à 6 chiffres par email. Il doit le saisir pour confirmer. Tant que ce n'est pas fait, vous n'avez rien à faire." },
  { code: "NEW", label: "Nouvelle demande", color: "bg-blue-50 text-blue-800 border-blue-200", desc: "Le code a été validé. C'est à vous de jouer : confirmer la demande et fixer une date de livraison. Un email sera envoyé au client." },
  { code: "CONFIRMED", label: "Confirmée", color: "bg-yellow-50 text-yellow-800 border-yellow-200", desc: "Vous avez fixé la date. La livraison est prévue mais pas encore démarrée. Le client a été notifié." },
  { code: "IN_PROGRESS", label: "En cours", color: "bg-orange-50 text-orange-800 border-orange-200", desc: "Vous avez démarré la livraison. Utilisez le mode livraison sur place pour faire signer le BL et générer le PDF." },
  { code: "DELIVERED", label: "Livrée", color: "bg-green-50 text-green-800 border-green-200", desc: "Le bon de livraison a été signé. La commande est terminée. Vous pouvez télécharger ou renvoyer le BL par email au client, puis la marquer « Facturée » une fois votre facture émise." },
  { code: "INVOICED", label: "Facturée", color: "bg-teal-50 text-teal-800 border-teal-200", desc: "Vous avez émis votre facture pour cette livraison. Suivi interne : les commandes « Livrée » restantes sont celles qu'il vous reste à facturer." },
  { code: "CANCELLED", label: "Annulée", color: "bg-red-50 text-red-800 border-red-200", desc: "La commande a été annulée (par le client avant confirmation, ou par vous à tout moment). Aucune action possible." },
];

function StatusGuideButton() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button data-testid="status-guide-button" className="lc-btn-secondary lc-btn-sm">
          <HelpCircle className="w-3.5 h-3.5" /> Guide des statuts
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="display text-xl">Guide des statuts de commande</DialogTitle>
          <p className="text-sm text-[var(--lc-gray)] mt-1">Le funnel suit cet ordre, du premier calcul du client jusqu'à la livraison finale.</p>
        </DialogHeader>
        <div className="space-y-3 mt-4">
          {STATUS_GUIDE.map((s) => (
            <div key={s.code} className="lc-card p-3">
              <span className={`inline-flex items-center px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wider border ${s.color} mb-1.5`}>
                {s.label}
              </span>
              <p className="text-sm text-[var(--lc-dark-gray)]">{s.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 p-3 bg-[var(--lc-orange-soft)] border-l-4 border-[var(--lc-orange)] rounded text-xs">
          <strong>Astuce :</strong> Le statut peut uniquement avancer dans le funnel (jamais reculer). Une commande livrée ou annulée est figée. Vous pouvez archiver n'importe quelle commande pour la sortir de votre vue principale sans la supprimer.
        </div>
      </DialogContent>
    </Dialog>
  );
}

const fmtPrice = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n || 0);
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString("fr-FR"); } catch { return "-"; } };

const FILTERS = [
  { key: "UPCOMING", label: "À venir", statuses: ["CONFIRMED", "IN_PROGRESS"] },
  { key: "NEW", label: STATUS_LABELS.NEW, statuses: ["NEW"] },
  { key: "TO_FINALIZE", label: "À finaliser", statuses: ["DRAFT", "PENDING_OTP"] },
  { key: "CONFIRMED", label: STATUS_LABELS.CONFIRMED, statuses: ["CONFIRMED"] },
  { key: "IN_PROGRESS", label: STATUS_LABELS.IN_PROGRESS, statuses: ["IN_PROGRESS"] },
  { key: "DELIVERED", label: "Livrée (à facturer)", statuses: ["DELIVERED"] },
  { key: "INVOICED", label: "Facturée", statuses: ["INVOICED"] },
  { key: "CANCELLED", label: STATUS_LABELS.CANCELLED, statuses: ["CANCELLED"] },
  { key: "ALL", label: "Toutes", statuses: null },
  { key: "ARCHIVES", label: "Archives", statuses: null, archived: true },
];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("UPCOMING");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [acting, setActing] = useState(false);

  const cfg = FILTERS.find((x) => x.key === filter);
  const isArchives = !!cfg?.archived;

  const fetchAll = async (f) => {
    setLoading(true);
    setSelected(new Set());
    const c = FILTERS.find((x) => x.key === f);
    const base = { archived: c?.archived ? "true" : undefined };
    let results;
    if (!c.statuses) {
      results = (await api.get("/orders", { params: base })).data;
    } else if (c.statuses.length === 1) {
      results = (await api.get("/orders", { params: { ...base, status: c.statuses[0] } })).data;
    } else {
      const parts = await Promise.all(c.statuses.map((s) => api.get("/orders", { params: { ...base, status: s } }).then((r) => r.data)));
      results = parts.flat().sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
    setOrders(results);
    setLoading(false);
  };

  useEffect(() => { fetchAll(filter); }, [filter]);

  const toggleOne = (id) => setSelected((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const toggleAll = () => setSelected((s) => s.size === orders.length ? new Set() : new Set(orders.map((o) => o.id)));

  const doArchive = async (archive) => {
    if (selected.size === 0) return;
    const verb = archive ? "archiver" : "restaurer";
    if (!window.confirm(`${archive ? "Archiver" : "Restaurer"} ${selected.size} commande(s) ?`)) return;
    setActing(true);
    try {
      await api.post("/admin/orders/archive", { ids: Array.from(selected), archive });
      await fetchAll(filter);
    } catch (e) {
      alert(formatApiError(e));
    } finally {
      setActing(false);
    }
  };

  const doDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`⚠️ Supprimer DÉFINITIVEMENT ${selected.size} commande(s) ?\n\nCette action est irréversible : les commandes, leur historique et leurs fichiers (BL, bons de commande) seront perdus.`)) return;
    if (!window.confirm("Dernière confirmation : supprimer définitivement ?")) return;
    setActing(true);
    try {
      const { data } = await api.post("/admin/orders/bulk-delete", { ids: Array.from(selected) });
      alert(`${data.deleted} commande(s) supprimée(s) définitivement.`);
      await fetchAll(filter);
    } catch (e) {
      alert(formatApiError(e));
    } finally {
      setActing(false);
    }
  };

  const currentFilterLabel = cfg?.label || "";

  return (
    <div data-testid="admin-orders-page">
      <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="lc-small-label mb-1">Back-office</div>
          <h1 className="display text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3">
            <History className="w-7 h-7 text-[var(--lc-orange)]" />
            Commandes
          </h1>
        </div>
        <StatusGuideButton />
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2 mb-2">
        {FILTERS.map((f) => {
          const isActive = filter === f.key;
          return (
            <button
              key={f.key}
              data-testid={`admin-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`shrink-0 px-3 py-1.5 text-xs font-semibold rounded-md border transition-colors ${
                isActive
                  ? "bg-[var(--lc-black)] text-white border-[var(--lc-black)]"
                  : "bg-white text-[var(--lc-dark-gray)] border-[var(--lc-border)] hover:border-[var(--lc-black)]"
              }`}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      <div className="mb-4 flex items-center justify-between gap-3 flex-wrap text-xs">
        <div className="text-[var(--lc-gray)]" data-testid="admin-filter-count">
          {loading ? "Chargement…" : (
            <>
              <span className="font-semibold text-[var(--lc-black)]">{orders.length}</span>{" "}
              {orders.length > 1 ? "résultats" : "résultat"} pour le filtre «&nbsp;{currentFilterLabel}&nbsp;»
            </>
          )}
        </div>
        {orders.length > 0 && (
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 font-semibold cursor-pointer">
              <input
                type="checkbox"
                data-testid="admin-orders-select-all"
                checked={selected.size === orders.length && orders.length > 0}
                onChange={toggleAll}
              />
              Tout sélectionner
            </label>
            {selected.size > 0 && (
              <>
                <button
                  data-testid="admin-orders-archive-button"
                  onClick={() => doArchive(!isArchives)}
                  disabled={acting}
                  className="lc-btn-primary lc-btn-sm"
                >
                  {isArchives ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                  {isArchives ? `Restaurer (${selected.size})` : `Archiver (${selected.size})`}
                </button>
                <button
                  data-testid="admin-orders-delete-button"
                  onClick={doDelete}
                  disabled={acting}
                  className="lc-btn-sm inline-flex items-center gap-1.5 font-bold text-red-600 border border-red-300 bg-red-50 hover:bg-red-100 rounded px-2.5 py-1.5"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Supprimer ({selected.size})
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {!loading && orders.length === 0 && (
        <div className="lc-card p-10 text-center">
          <div className="text-base font-semibold">Aucune commande</div>
        </div>
      )}

      <div className="grid gap-3" data-testid="admin-orders-list">
        {orders.map((o) => {
          const isSel = selected.has(o.id);
          return (
            <div
              key={o.id}
              data-testid={`admin-order-row-${o.id}`}
              className={`lc-card p-4 sm:p-5 grid grid-cols-[auto,1fr,auto] gap-3 items-center transition-colors ${
                isSel ? "border-[var(--lc-orange)] bg-[var(--lc-orange-soft)]" : "hover:border-[var(--lc-orange)]/40"
              }`}
            >
              <input
                type="checkbox"
                data-testid={`admin-order-select-${o.id}`}
                checked={isSel}
                onChange={() => toggleOne(o.id)}
                onClick={(e) => e.stopPropagation()}
              />
              <Link to={`/admin/orders/${o.id}`} className="min-w-0">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <span className="font-mono text-xs text-[var(--lc-gray)]">{o.ref}</span>
                  <StatusBadge status={o.status} />
                  {o.company_name && (
                    <span data-testid={`order-company-badge-${o.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[var(--lc-black)] bg-[var(--lc-surface-alt)] border border-[var(--lc-border)] px-1.5 py-0.5 rounded">
                      <Building2 className="w-3 h-3" />{o.company_name}
                    </span>
                  )}
                  {o.vendor_name && (
                    <span data-testid={`order-vendor-badge-${o.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[var(--lc-orange)] bg-[var(--lc-orange-soft)] border border-orange-200 px-1.5 py-0.5 rounded">
                      <UserCircle className="w-3 h-3" />{o.vendor_name}
                    </span>
                  )}
                  {o.archived && <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--lc-gray)] bg-[var(--lc-surface-alt)] px-1.5 py-0.5 rounded">Archivée</span>}
                </div>
                <div className="text-sm font-semibold flex items-start gap-1.5">
                  <MapPin className="w-3.5 h-3.5 mt-0.5 text-[var(--lc-orange)] shrink-0" />
                  {o.arrival_address}
                </div>
                <div className="text-xs text-[var(--lc-gray)] mt-1">
                  {fmtDate(o.delivery_date_confirmed || o.delivery_date_wanted)} · {o.distance_km} km · {o.package_name}
                </div>
              </Link>
              <Link to={`/admin/orders/${o.id}`} className="flex flex-col items-end gap-1">
                <div className="text-xl font-black text-[var(--lc-orange)]">{o.on_quote ? "Sur devis" : fmtPrice(o.total_ttc)}</div>
                <ChevronRight className="w-4 h-4 text-[var(--lc-gray)]" />
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  );
}
